import React from "react";
import { Card } from "./ui/card";
import { Phone, Lightbulb, HeadphonesIcon, ArrowRight } from "lucide-react";

const HowWeWork = () => {
  const steps = [
    {
      phase: "Fase inicial",
      title: "Análisis estratégico",
      description: "Realizamos un diagnóstico completo de tu empresa para identificar las áreas de mayor impacto donde la IA puede generar valor inmediato y sostenible.",
      features: [
        "Evaluamos tu estructura operativa actual",
        "Detectamos procesos susceptibles de automatización",
        "Diseñamos una hoja de ruta personalizada"
      ],
      icon: <Phone className="h-8 w-8 text-purple-600" />,
      image: "https://framerusercontent.com/images/6Whdu7PT3lJskPnkSJUtQ4blRk.jpg"
    },
    {
      phase: "Desarrollo",
      title: "Implementación progresiva",
      description: "Construimos tu solución paso a paso, validando cada funcionalidad contigo para asegurar que cumple exactamente con tus necesidades específicas.",
      features: [
        "Desarrollo iterativo con validación continua",
        "Pruebas en entorno real con datos reales",
        "Ajustes precisos basados en tu feedback"
      ],
      icon: <Lightbulb className="h-8 w-8 text-orange-600" />,
      image: "https://framerusercontent.com/images/03xxzX7tFg0pOAnRQNPl0JkpYQ.jpg"
    },
    {
      phase: "Evolución",
      title: "Optimización continua",
      description: "Mantenemos tu sistema actualizado y optimizado, adaptándolo a los cambios de tu negocio y aprovechando las nuevas oportunidades que surgen.",
      features: [
        "Monitoreo constante del rendimiento",
        "Actualizaciones y mejoras regulares",
        "Expansión de funcionalidades según tu crecimiento"
      ],
      icon: <HeadphonesIcon className="h-8 w-8 text-green-600" />,
      image: "https://framerusercontent.com/images/WmRtO1KxHMUpouSiTABIvVrTfsk.jpg"
    }
  ];

  return (
    <section id="proceso" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-800 mb-4">
            Nuestro proceso de{" "}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              transformación digital
            </span>
          </h2>
        </div>

        {/* Steps */}
        <div className="space-y-20">
          {steps.map((step, index) => (
            <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="inline-block px-4 py-2 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                  {step.phase}
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gray-50 rounded-xl">
                    {step.icon}
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-800">
                    {step.title}
                  </h3>
                </div>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  {step.description}
                </p>
                
                <ul className="space-y-3">
                  {step.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="relative group">
                  <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-0 group-hover:opacity-100"></div>
                  <img 
                    src={step.image}
                    alt={step.title}
                    className="relative w-full h-80 object-cover rounded-2xl shadow-2xl group-hover:shadow-3xl transition-all duration-300 transform group-hover:scale-105"
                  />
                </div>
              </div>
              
              {/* Arrow connector for non-last items */}
              {index < steps.length - 1 && (
                <div className="col-span-full flex justify-center mt-12">
                  <div className="flex items-center space-x-2 text-gray-400">
                    <div className="w-12 h-0.5 bg-gray-300"></div>
                    <ArrowRight className="h-5 w-5" />
                    <div className="w-12 h-0.5 bg-gray-300"></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;